import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, X } from "lucide-react";

interface SubscriptionData {
  stripeSubscriptionId: string;
  cancelAtPeriodEnd: boolean;
  items: { stripePriceId: string }[];
  trial: boolean;
  trialEndDate: string;
  phone: boolean;
  groupToken: string;
  groupSubscription: string;
}

const emptySubscription = (): SubscriptionData => ({
  stripeSubscriptionId: "",
  cancelAtPeriodEnd: false,
  items: [],
  trial: false,
  trialEndDate: "",
  phone: false,
  groupToken: "",
  groupSubscription: "",
});

export function ArtistEditSubscription({ artistId }: { artistId: string }) {
  const [subscription, setSubscription] = useState<SubscriptionData>(emptySubscription());
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/admin/artists/${artistId}/subscription`);

        if (response.ok) {
          const data = await response.json();
          setUserId(data.userId ?? null);
          if (data.subscription) {
            setSubscription({
              stripeSubscriptionId: data.subscription.stripeSubscriptionId ?? "",
              cancelAtPeriodEnd: data.subscription.cancelAtPeriodEnd ?? false,
              items: data.subscription.items ?? [],
              trial: data.subscription.trial ?? false,
              trialEndDate: data.subscription.trialEndDate ?? "",
              phone: data.subscription.phone ?? false,
              groupToken: data.subscription.groupToken ?? "",
              groupSubscription: data.subscription.groupSubscription ?? "",
            });
          } else {
            setSubscription(emptySubscription());
          }
        } else {
          console.error("Failed to fetch subscription information");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [artistId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`/api/admin/artists/${artistId}/subscription`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscription),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save subscription");
      }

      alert("Subscription saved successfully");
    } catch (error) {
      console.error("Error saving subscription:", error);
      alert(error instanceof Error ? error.message : "Failed to save subscription");
    } finally {
      setIsSaving(false);
    }
  };

  const updateItem = (index: number, stripePriceId: string) => {
    const items = [...subscription.items];
    items[index] = { stripePriceId };
    setSubscription({ ...subscription, items });
  };

  const addItem = () => {
    setSubscription({
      ...subscription,
      items: [...subscription.items, { stripePriceId: "" }],
    });
  };

  const removeItem = (index: number) => {
    setSubscription({
      ...subscription,
      items: subscription.items.filter((_, i) => i !== index),
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {userId && (
        <p className="text-sm text-muted-foreground">User ID: {userId}</p>
      )}

      <div className="space-y-2">
        <Label htmlFor="stripeSubscriptionId">Stripe Subscription ID</Label>
        <Input
          id="stripeSubscriptionId"
          value={subscription.stripeSubscriptionId}
          onChange={(e) =>
            setSubscription({ ...subscription, stripeSubscriptionId: e.target.value })
          }
          placeholder="sub_..."
        />
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="trial">Trial</Label>
        <Switch
          id="trial"
          checked={subscription.trial}
          onCheckedChange={(checked) =>
            setSubscription({ ...subscription, trial: checked })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="trialEndDate">Trial End Date</Label>
        <Input
          id="trialEndDate"
          type="date"
          value={subscription.trialEndDate}
          onChange={(e) =>
            setSubscription({ ...subscription, trialEndDate: e.target.value })
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="cancelAtPeriodEnd">Cancel at Period End</Label>
        <Switch
          id="cancelAtPeriodEnd"
          checked={subscription.cancelAtPeriodEnd}
          onCheckedChange={(checked) =>
            setSubscription({ ...subscription, cancelAtPeriodEnd: checked })
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="phone">Phone</Label>
        <Switch
          id="phone"
          checked={subscription.phone}
          onCheckedChange={(checked) =>
            setSubscription({ ...subscription, phone: checked })
          }
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="groupToken">Group Token</Label>
        <Input
          id="groupToken"
          value={subscription.groupToken}
          onChange={(e) =>
            setSubscription({ ...subscription, groupToken: e.target.value })
          }
          placeholder="Group token"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="groupSubscription">Group Subscription ID</Label>
        <Input
          id="groupSubscription"
          value={subscription.groupSubscription}
          onChange={(e) =>
            setSubscription({ ...subscription, groupSubscription: e.target.value })
          }
          placeholder="Subscription ObjectId"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Stripe Price IDs</Label>
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        {subscription.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No price IDs added.</p>
        ) : (
          <div className="space-y-2">
            {subscription.items.map((item, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item.stripePriceId}
                  onChange={(e) => updateItem(index, e.target.value)}
                  placeholder="price_..."
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeItem(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
}
